import { trackEvent, AnalyticsEvents } from '../../analytics';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Button, Group, Menu, Tooltip } from '@mantine/core';
import { IconDots, IconSparkles } from '@tabler/icons-react';
import { API_URL, api } from '../../utils/api';
import { useUserStore } from '../../store/userStore';
import { showToast } from '../../utils/toast';

/**
 * Копирует готовый промпт для локального ИИ: адрес API, ключ ассистента, порядок
 * запросов и формат тела. Так эссе можно продиктовать своему ассистенту, не
 * заводя ни MCP-сервера, ни отдельных ключей. В промпт уходит не сессия, а ключ
 * ассистента: только свои эссе, 7 дней, отзывается из меню рядом.
 */
export const CopyEssayPromptButton: React.FC = () => {
  const { t } = useTranslation();
  const isSignedIn = useUserStore((s) => !!s.token);
  const [isBusy, setIsBusy] = useState(false);
  // API_URL пуст, когда фронт и бэк на одном origin (и в локальной разработке).
  const apiUrl = API_URL || window.location.origin;

  if (!isSignedIn) return null;

  const handleCopy = async () => {
    setIsBusy(true);
    const prompt = api('/essays/assistant-token', { method: 'POST' }).then(
      ({ token }: { token: string }) => t('essays.aiPrompt', { apiUrl, token }),
    );
    try {
      // Safari пускает в буфер обмена только сразу по клику: запись с обещанием
      // текста начинается до ответа сервера. Где ClipboardItem нет — пишем после.
      if (typeof ClipboardItem !== 'undefined') {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': prompt.then((text) => new Blob([text], { type: 'text/plain' })),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(await prompt);
      }
      trackEvent(AnalyticsEvents.ESSAY_AI_PROMPT_COPIED);
      showToast({
        title: t('essays.aiPromptCopied'),
        message: t('essays.aiPromptTokenWarning'),
        color: 'teal',
      });
    } catch {
      // Сервер не выдал ключ, или браузер не дал доступ к буферу обмена.
      showToast({
        title: t('essays.aiPromptFailed'),
        message: t('essays.aiPromptFailedMessage'),
        color: 'red',
      });
    } finally {
      setIsBusy(false);
    }
  };

  const handleRevoke = async () => {
    try {
      await api('/essays/assistant-token', { method: 'DELETE' });
      showToast({
        title: t('essays.aiKeyRevoked'),
        message: t('essays.aiKeyRevokedMessage'),
        color: 'teal',
      });
    } catch {
      showToast({
        title: t('essays.aiKeyRevokeFailed'),
        message: t('essays.aiKeyRevokeFailedMessage'),
        color: 'red',
      });
    }
  };

  return (
    <Group gap={4} wrap="nowrap">
      <Tooltip label={t('essays.aiPromptTokenWarning')} withArrow multiline w={260}>
        <Button
          variant="default"
          leftSection={<IconSparkles size={16} />}
          onClick={handleCopy}
          loading={isBusy}
        >
          {t('essays.aiPromptCopy')}
        </Button>
      </Tooltip>
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="default" size="lg" aria-label={t('essays.aiKeyMenu')}>
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item color="red" onClick={handleRevoke}>
            {t('essays.aiKeyRevoke')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
};
